/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-shadow */
/* eslint-disable @typescript-eslint/indent */
import React, { useEffect, useState, useMemo } from 'react';
import logger from '@/logger';
import Head from 'next/head';
import { useUTMParameters } from '@pwskills/rachnaui/hooks';
import Loader from '@/components/common/Loader';
import { ICourseDescription, IMappedCourseDetails } from '@/interfaces/courseDescription';
import CourseDescription from '@/api/courseDescription';
import CourseDescriptionContext from '@/context/CourseDescriptionContext';
import { companyImage, domainUrl } from '@/utils/common/env.constants';
import { IError } from '@/interfaces/common';
import { getPriceDetails, setCookies, setPPToken } from '@/utils/helper/helperFunction';
import { dataLayer } from '@/utils/common/dataLayer.utils';
import moEvent from '@/utils/moEngage';
import { useRouter } from 'next/router';
import useIsCourseBought from '@/hooks/useIsCourseBought';
import { COURSE_PURCHASE_TYPE } from '@/utils/common/common.constants';
import CategoryPageApi from '@/api/categoryPage';
// import useUserCourseDetails from '@/store/userCoursesDetails';
// import useKapture from '@/hooks/useKapture';
import useSlugTitleMapFunction from '@/store/useSlugTitleMap';
import useGetUserAllCourses from '@/hooks/useGetUserAllCourses';
import { CourseStatusEnums } from '@/components/PostLoginOnboarding/Utilities/enums';
import useGetUserProfileDetails from '@/hooks/useGetUserProfileDetails';
import Common from '@/api/common';
import NsdcEnrolmentLoader from '@/components/Assignment/common/NsdcEnrolmentLoader';
import { toast } from 'react-toastify';
import { cacheCourseBatchMetaFromCourseDetails } from '@/utils/courseBatchMetaCache';
import getCourseCanonicalUrl from '@/utils/courseCanonical.utils';
import { getNavigationContext } from '@/utils/navigationContext';
import { IFaqsDetails } from '../../interfaces/homePage';
import CourseDescriptionPageProfessional from '../../components/CourseDescriptionPageProfessional';
import CourseDescriptionPageAmateur from '../../components/CourseDescriptionPageAmature';
import ErrorPage from '../404';
import useUserDetails from '../../store/userDetails';

const CoursePage = ({
  response,
  slug,
  canonicalUrl,
}: {
  response: any;
  slug: string;
  canonicalUrl: string;
}) => {
  // const { userCoursesMap } = useUserCourseDetails();
  const [courseDetails, setCourseDetails] = useState<ICourseDescription | null>(null);
  // useKapture();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCourseBought, setIsCourseBought] = useState<boolean>(false);
  const [lessonId, setLessonId] = useState('');
  const { getUserDetails } = useGetUserProfileDetails();
  const [isNsdcLoading, setIsNSDCLoading] = useState<boolean>(false);
  const [mappedCourseDetails, setMappedCourseDetails] = useState<IMappedCourseDetails>({
    courseId: '',
    courseSlug: '',
  });
  // const { isLoggedIn, user } = useContext(UserDetailsContext);
  const { userDetails: user, userIsLoggedIn: isLoggedIn } = useUserDetails();
  const utmQuery = useUTMParameters();
  const courseId = courseDetails?._id as string;
  const router = useRouter();
  const { token } = router.query;
  const navigationContext = getNavigationContext(router.asPath);
  const { getUserCoursesFunction } = useGetUserAllCourses();
  const {
    checkIsCourseBought,
    returnLessonId,
    handleMappedCourses,
    isCourseBoughtForPlan,
    planCourseMappedDetails,
  } = useIsCourseBought();
  const { slugTitleMap, setSlugTitleMap } = useSlugTitleMapFunction();
  // Get the canonical URL based on the value from the query or any other source
  const payload = {
    courseId,
    ...utmQuery,
  };

  const sendUserActivity = async () => {
    try {
      await CourseDescription.trackUserCourseActivity(payload);
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error((error as IError)?.error);
    }
  };

  const useStore = useMemo(() => {
    return {
      courseDetails,
      isCourseBought,
      setCourseDetails,
      loading: isLoading,
      setIsLoading,
      lessonId,
      mappedCourseDetails,
    };
  }, [courseDetails, isCourseBought, isLoading, lessonId, mappedCourseDetails]);

  const DataLayerParam = () => {
    const {
      _id,
      name,
      title,
      startDate,
      categoryDetails,
      courseMetaDetails,
      programType,
      batchCategoryId,
    } = courseDetails || {};
    const pricing = courseMetaDetails?.pricing;
    const priceDetails = getPriceDetails(pricing);
    const DataLayerCourseDescription = {
      course_name: name || title || null,
      course_id: _id,
      category_id: batchCategoryId || categoryDetails?._id,
      source: (router?.query?.position as string) || navigationContext.position,
      page_ref: (router?.query?.from as string) || navigationContext.from,
      program_type: programType?.join(', ') || null,
      domain: categoryDetails?.name || null,
      language: courseMetaDetails?.overview?.language || null,
      amount: priceDetails?.currentPrice || null,
      discountPercent: priceDetails?.discount || null,
      start_on: startDate || null,
      duration_month: courseMetaDetails?.duration || null,
      course_rating: null, // ratingAverage not available in new structure
      rating_count: null, // ratingCount not available in new structure
      category: categoryDetails?.name || null,
      sub_category: null, // sub_category not available in new structure
    };
    dataLayer({ ...DataLayerCourseDescription, page_name: 'course_desc', event: 'course_desc' });
  };
  const hanleSlugTitleMap = (slug: string, title: string) => {
    const tempObj = { ...slugTitleMap, [slug]: title };
    setSlugTitleMap(tempObj);
  };

  useEffect(() => {
    if (slug) {
      hanleSlugTitleMap(slug, response?.data?.name || response?.data?.title);
    }
    setCourseDetails(response.data);
    cacheCourseBatchMetaFromCourseDetails(response?.data);
  }, [slug]);

  useEffect(() => {
    const fetchNSDCData = async () => {
      const params = new URLSearchParams(window.location.search);
      try {
        setIsNSDCLoading(true);
        const nsdcDecrypt: any = await Common.nsdcEnrolment(courseId, token as string);
        const newUrl =
          window.location.pathname +
          (params.toString() ? `?${params.toString()}` : '') +
          window.location.hash;
        window.history.replaceState(null, '', newUrl);
        if (nsdcDecrypt) {
          params.delete('token');
          await Promise.all([
            setPPToken(nsdcDecrypt?.data?.ppToken?.access_token),
            setCookies('ppToken', nsdcDecrypt?.data?.ppToken?.access_token),
          ]);
          localStorage.setItem('shouldUserRedirectToDashboard', 'true');
          setTimeout(() => {
            getUserDetails();
            window.location.href = '/dashboard/mycourse';
          }, 1000);
        }
      } catch (error: unknown) {
        toast.error((error as IError)?.error || 'Something went wrong');
      } finally {
        setIsNSDCLoading(false);
      }
    };
    if (token && courseId) fetchNSDCData();
  }, [token, courseId]);

  useEffect(() => {
    if (courseDetails?._id) {
      if (isLoggedIn && !token) {
        getUserCoursesFunction().then(() => {
          if (courseDetails?.coursePurchaseType === COURSE_PURCHASE_TYPE.PLAN) {
            setIsCourseBought(
              isCourseBoughtForPlan(courseDetails?.courseMetaDetails?.coursePlansDetails)
            );
            setMappedCourseDetails(
              planCourseMappedDetails(courseDetails?.courseMetaDetails?.coursePlansDetails as any)
            );
          } else {
            setIsCourseBought(checkIsCourseBought(courseId as string));
            setMappedCourseDetails(handleMappedCourses(courseId as string));
          }
        });
        // getIsCourseBought();

        setLessonId(returnLessonId(courseId as string));
        sendUserActivity();
      }
      DataLayerParam();
    }
  }, [courseDetails?._id, isLoggedIn]);

  // moEngage Event
  useEffect(() => {
    if (courseDetails?._id && router?.isReady && user?._id) {
      const moEventData = {
        user_id: user?._id || '',
        phone_number: user?.phoneNumber || '',
        user_type: isLoggedIn ? 'loggedin' : 'not_loggedin',
        course_name: courseDetails?.name || courseDetails?.title || '',
        course_id: courseDetails?._id || '',
        course_status: isLoggedIn && isCourseBought ? 'purchased' : 'not_purchased',
        category_name: courseDetails?.categoryDetails?.name || '',
        course_type: courseDetails?.coursePurchaseType || '',
        course_price_tag:
          courseDetails?.courseMetaDetails?.pricing?.isFree === true ? 'Free' : 'Paid',
        course_slug: courseDetails?.slug || '',
        program_type: courseDetails?.programType?.toString().replaceAll(',', ', ') || '',
        source_page_URL: 'https://pwskills.com',
        source_position:
          (router?.query?.source_position as string) ||
          // dropdown has position
          (router?.query?.from as string) ||
          (router?.query?.position as string) ||
          navigationContext.from ||
          navigationContext.position ||
          '',
        ...(user?.verified && user?.email && { email: user.email }),
      };
      moEvent('course_description_page_view', moEventData);
    }
  }, [courseDetails?._id, user, router?.isReady]);

  const fetchStartDate = () => {
    const istFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kolkata' });
    return response?.data?.startDate && istFormatter?.format(new Date(response?.data?.startDate));
  };

  const structData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: response?.data?.courseMetaDetails?.faq?.map((faq: IFaqsDetails) => {
      return {
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq?.answer,
        },
      };
    }),
  };

  const strucDataBreadCrumb = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://pwskills.com',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: `${response?.data?.name || response?.data?.title}`,
          item: `https://pwskills.com/courses/${response?.data?.slug}`,
        },
      ],
    },
  ];

  const structDataVideo = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: response.data.name || response.data.title,
    description: response.data.description,
    // upload thumbnail logic is updated - newer uploads will return s3 bucket url and older one will return id
    thumbnailUrl:
      response?.data?.courseMetaDetails?.about?.thumbnailUrl &&
      response?.data?.courseMetaDetails?.about?.thumbnailUrl.startsWith('https')
        ? response?.data?.courseMetaDetails?.about?.thumbnailUrl
        : response?.data?.img && response?.data?.img.startsWith('https')
        ? response?.data?.img
        : `${companyImage}/thumbnails/${response?.data?.img || 'default'}`,
    uploadDate: response.data?.registrationStartDate,
    duration: '',
    contentUrl: response.data.courseMetaDetails?.videoURL || '',
    embedUrl: response.data.courseMetaDetails?.videoURL || '',
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'WatchAction' },
      userInteractionCount: '',
    },
    regionsAllowed: 'IN,US',
  };
  const structDataEducationEvent = {
    '@context': 'http://schema.org',
    '@type': 'EducationEvent',
    name: response.data.name || response.data.title,
    description: response.data.description,
    image: response.data.img,
    performer: 'PW Skills',
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: response.data?.courseMetaDetails?.pricing?.IN
        ? Math.floor(
            (response.data?.courseMetaDetails?.pricing?.IN || 0) -
              (Math.abs(response.data?.courseMetaDetails?.pricing?.discount || 0) *
                (response.data?.courseMetaDetails?.pricing?.IN || 0)) /
                100
          )
        : 0,
      highPrice: response.data?.courseMetaDetails?.pricing?.IN || 0,
      url: `https://pwskills.com/course/${response.data.slug}/`,
      availability: 'https://schema.org/InStock',
      price: response.data?.courseMetaDetails?.pricing?.IN
        ? Math.floor(
            (response.data?.courseMetaDetails?.pricing?.IN || 0) -
              (Math.abs(response.data?.courseMetaDetails?.pricing?.discount || 0) *
                (response.data?.courseMetaDetails?.pricing?.IN || 0)) /
                100
          )
        : 0,
      priceCurrency: 'INR',
      validFrom: '',
    },
    startDate: fetchStartDate(),
    endDate: response.data.endDate,
    url: `https://pwskills.com/course/${response.data.slug}/`,
    duration: response?.data?.courseMetaDetails?.duration || '',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    eventStatus: 'http://schema.org/EventScheduled',
    location: {
      '@type': 'VirtualLocation',
      url: `https://pwskills.com/course/${response.data.slug}/`,
    },
    organizer: {
      '@type': 'Organization',
      name: 'PW Skills',
      url: 'https://pwskills.com/',
    },
  };

  const structCourseList = {
    '@id': '',
    '@context': 'https://schema.org',
    type: 'Course',
    name: `${response?.data?.name || response?.data?.title}`,
    description: `${response?.data?.courseMetaDetails?.seo?.description}`,
    provider: {
      '@type': 'Organization',
      name: 'PW Skills',
      sameAs: `https://pwskills.com/course/${response?.data?.slug}`,
    },
    offers: [
      {
        '@type': 'Offer',
        category: 'Paid',
        priceCurrency: 'INR',
        price: response?.data.courseMetaDetails?.pricing?.IN
          ? Math.floor(
              (response.data?.courseMetaDetails?.pricing?.IN || 0) -
                (Math.abs(response?.data.courseMetaDetails?.pricing?.discount || 0) *
                  (response.data?.courseMetaDetails?.pricing?.IN || 0)) /
                  100
            )
          : 0,
      },
    ],
    hasCourseInstance: [
      {
        '@type': 'CourseInstance',
        courseMode: 'Online',
        courseWorkload: 'P2D',
      },
    ],
  };
  return (
    <>
      <Head>
        <meta
          name="description"
          content={
            response?.data?.courseMetaDetails?.seo?.description ||
            response?.data?.shortDescription ||
            response?.data?.description ||
            'Learn with PW Skills'
          }
        />

        {/* Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:image"
          content={
            response?.data?.courseMetaDetails?.seo?.ogImage &&
            response?.data?.courseMetaDetails?.seo?.ogImage.trim() !== '' &&
            response?.data?.courseMetaDetails?.seo?.ogImage !== ' '
              ? response?.data?.courseMetaDetails?.seo?.ogImage
              : response?.data?.courseMetaDetails?.about?.thumbnailUrl &&
                response?.data?.courseMetaDetails?.about?.thumbnailUrl.trim() !== '' &&
                response?.data?.courseMetaDetails?.about?.thumbnailUrl !== ' '
              ? response?.data?.courseMetaDetails?.about?.thumbnailUrl
              : response?.data?.img && response?.data?.img.startsWith('https')
              ? response?.data?.img
              : `${companyImage}/thumbnails/${response?.data?.img || 'default'}`
          }
        />
        <meta
          property="og:title"
          content={
            response?.data?.courseMetaDetails?.seo?.ogTitle
              ? response?.data?.courseMetaDetails?.seo?.ogTitle
              : response?.data?.courseMetaDetails?.seo?.title ||
                response?.data?.name ||
                response?.data?.title
          }
        />
        <meta
          property="og:description"
          content={
            response?.data?.courseMetaDetails?.seo?.ogDescription
              ? response?.data?.courseMetaDetails?.seo?.ogDescription
              : response?.data?.courseMetaDetails?.seo?.description ||
                response?.data?.shortDescription ||
                response?.data?.description
          }
        />
        <meta property="og:image:width" content="456" />
        <meta property="og:image:height" content="256" />

        {/* Twitter */}
        <meta property="twitter:url" content={canonicalUrl} />
        <meta
          property="twitter:title"
          content={
            response?.data?.courseMetaDetails?.seo?.twitterTitle
              ? response?.data?.courseMetaDetails?.seo?.twitterTitle
              : response?.data?.courseMetaDetails?.seo?.title ||
                response?.data?.name ||
                response?.data?.title
          }
        />
        <meta
          property="twitter:description"
          content={
            response?.data?.courseMetaDetails?.seo?.twitterDescription
              ? response?.data?.courseMetaDetails?.seo?.twitterDescription
              : response?.data?.courseMetaDetails?.seo?.description ||
                response?.data?.shortDescription ||
                response?.data?.description
          }
        />
        <meta
          property="twitter:image"
          content={
            response?.data?.courseMetaDetails?.seo?.twitterImage &&
            response?.data?.courseMetaDetails?.seo?.twitterImage.trim() !== '' &&
            response?.data?.courseMetaDetails?.seo?.twitterImage !== ' '
              ? response?.data?.courseMetaDetails?.seo?.twitterImage
              : response?.data?.courseMetaDetails?.about?.thumbnailUrl &&
                response?.data?.courseMetaDetails?.about?.thumbnailUrl.trim() !== '' &&
                response?.data?.courseMetaDetails?.about?.thumbnailUrl !== ' '
              ? response?.data?.courseMetaDetails?.about?.thumbnailUrl
              : response?.data?.img && response?.data?.img.startsWith('https')
              ? response?.data?.img
              : `${companyImage}/thumbnails/${response?.data?.img || 'default'}`
          }
        />
        <meta property="twitter:image:width" content="456" />
        <meta property="twitter:image:height" content="256" />
        <script
          key="course-description-faqs"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structData) }}
        />
        <script
          key="course-description-breadcrumbs"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(strucDataBreadCrumb) }}
        />
        <script
          key="course-description-video-schema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structDataVideo) }}
        />
        <script
          key="course-description-education-event"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structDataEducationEvent) }}
        />
        <script
          key="course-description-course-list"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structCourseList) }}
        />
      </Head>
      {/* description page */}
      {courseDetails ? (
        courseDetails.status === CourseStatusEnums.ACTIVE ? (
          <>
            <CourseDescriptionContext.Provider value={useStore}>
              {response?.data?.courseMetaDetails?.pageTheme === 'professional' ? (
                <CourseDescriptionPageProfessional />
              ) : (
                <CourseDescriptionPageAmateur />
              )}
            </CourseDescriptionContext.Provider>
            <NsdcEnrolmentLoader open={isNsdcLoading} />
          </>
        ) : (
          <ErrorPage />
        )
      ) : (
        <Loader />
      )}
    </>
  );
};

export const getStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' };
};

export async function getStaticProps({
  params,
}: {
  params: { categorySlug: string; courseSlug: string };
}) {
  try {
    const redirectUrl = await CourseDescription.getServerSideRedirectUrl(
      `${domainUrl}/${params.categorySlug}/${params.courseSlug}`
    );
    if (redirectUrl) {
      return {
        redirect: { destination: redirectUrl, permanent: true },
        revalidate: 10,
      };
    }

    const categoryResponse: any = await CategoryPageApi.getCategoryData(params.categorySlug);
    logger.info(`GET /category/${params.categorySlug} PAGE: course`);
    if (categoryResponse.success) {
      const response = await CourseDescription.getCourseDescription(params.courseSlug);
      logger.info(`GET /course/${params.courseSlug} PAGE: course`);

      // Check if course matches category using the new API structure
      if (response?.data?.categoryDetails?.slug !== params?.categorySlug) {
        logger.info(
          `Provided course "${params.courseSlug}" is not mapped to category "${params.categorySlug}". Course category: "${response?.data?.categoryDetails?.slug}". PAGE: course`
        );
        // Allow the course to load even if category doesn't match for now
        // return { notFound: true, revalidate: 1 };
      }

      return {
        props: {
          slug: params.courseSlug,
          canonicalUrl: getCourseCanonicalUrl(
            response?.data,
            params.categorySlug,
            params.courseSlug
          ),
          response,
        },
        revalidate: 10,
      };
    }
    return { notFound: true, revalidate: 1 };
  } catch (error: any) {
    logger.error(`GET /course/${params.courseSlug} ${error.error || error} PAGE: course`);
    return { notFound: true, revalidate: 1 };
  }
}

export default CoursePage;
